"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DepositScript = exports.Deposit = void 0;
const contracts_1 = require("../../lib/contracts");
const bitcoin_1 = require("../../lib/bitcoin");
const bitcoinjs_lib_1 = require("bitcoinjs-lib");
const utils_1 = require("../../lib/utils");
/**
 * Component representing an instance of the tBTC v2 deposit process.
 * Depositing is a complex process spanning both the Bitcoin and the target chain.
 * This component tries to abstract away that complexity.
 */
class Deposit {
    constructor(receipt, tbtcContracts, bitcoinClient, bitcoinNetwork, depositorProxy) {
        this.script = DepositScript.fromReceipt(receipt);
        this.tbtcContracts = tbtcContracts;
        this.bitcoinClient = bitcoinClient;
        this.bitcoinNetwork = bitcoinNetwork;
        this.depositorProxy = depositorProxy;
    }
    static async fromReceipt(receipt, tbtcContracts, bitcoinClient, depositorProxy) {
        const bitcoinNetwork = await bitcoinClient.getNetwork();
        return new Deposit(receipt, tbtcContracts, bitcoinClient, bitcoinNetwork, depositorProxy);
    }
    /**
     * @returns Receipt corresponding to this deposit.
     */
    getReceipt() {
        return this.script.receipt;
    }
    /**
     * @returns Bitcoin address corresponding to this deposit.
     */
    async getBitcoinAddress() {
        return this.script.deriveAddress(this.bitcoinNetwork);
    }
    /**
     * Detects Bitcoin funding transactions transferring BTC to this deposit.
     * The list includes UTXOs from both the blockchain and the mempool, sorted by
     * age with the newest ones first. Mempool UTXOs are listed at the beginning.
     * @returns Specific UTXOs targeting this deposit. Empty array in case
     *         there are no UTXOs referring this deposit.
     */
    async detectFunding() {
        const utxos = await this.bitcoinClient.findAllUnspentTransactionOutputs(await this.getBitcoinAddress());
        if (!utxos || utxos.length === 0) {
            return [];
        }
        return utxos;
    }
    /**
     * Initiates minting of the TBTC token, based on the Bitcoin funding
     * transaction outpoint targeting this deposit. By default, it detects and
     * uses the outpoint of the recent Bitcoin funding transaction and throws if
     * such a transaction does not exist. This behavior can be changed by pointing
     * a funding transaction explicitly, using the fundingOutpoint parameter.
     * @param fundingOutpoint Optional parameter. Can be used to point
     *        the funding transaction's outpoint manually.
     * @returns Target chain hash of the initiate minting transaction.
     * @throws Throws an error if there are no funding transactions while using
     *         the default funding detection mode.
     * @throws Throws an error if the provided funding outpoint does not
     *         actually refer to this deposit while using the manual funding
     *         provision mode.
     * @throws Throws an error if the funding outpoint was already used to
     *         initiate minting (both modes).
     */
    async initiateMinting(fundingOutpoint) {
        let resolvedFundingOutpoint;
        if (typeof fundingOutpoint !== "undefined") {
            resolvedFundingOutpoint = fundingOutpoint;
        }
        else {
            const fundingUtxos = await this.detectFunding();
            if (fundingUtxos.length == 0) {
                throw new Error("Deposit not funded yet");
            }
            // Take the most recent one.
            resolvedFundingOutpoint = fundingUtxos[0];
        }
        const { transactionHash, outputIndex } = resolvedFundingOutpoint;
        const depositFundingTx = (0, bitcoin_1.extractBitcoinRawTxVectors)(await this.bitcoinClient.getRawTransaction(transactionHash));
        const { bridge, tbtcVault } = this.tbtcContracts;
        if (typeof this.depositorProxy !== "undefined") {
            return this.depositorProxy.revealDeposit(depositFundingTx, outputIndex, this.getReceipt(), tbtcVault.getChainIdentifier());
        }
        return bridge.revealDeposit(depositFundingTx, outputIndex, this.getReceipt(), tbtcVault.getChainIdentifier());
    }
}
exports.Deposit = Deposit;
/**
 * Represents a Bitcoin script corresponding to a tBTC v2 deposit.
 * On a high-level, the script is used to derive the Bitcoin address that is
 * used to fund the deposit with BTC. On a low-level, the script is used to
 * produce a properly locked funding transaction output that can be unlocked
 * by the target wallet during the deposit sweep process.
 */
class DepositScript {
    constructor(receipt, witness) {
        (0, contracts_1.validateDepositReceipt)(receipt);
        this.receipt = receipt;
        this.witness = witness;
    }
    static fromReceipt(receipt, witness = true) {
        return new DepositScript(receipt, witness);
    }
    /**
     * @returns Hashed deposit script as Buffer.
     */
    async getHash() {
        const script = await this.getPlainText();
        // If witness script hash should be produced, SHA256 should be used.
        // Legacy script hash needs HASH160.
        return this.witness
            ? bitcoin_1.BitcoinHashUtils.computeSha256(script).toBuffer()
            : bitcoin_1.BitcoinHashUtils.computeHash160(script).toBuffer();
    }
    /**
     * @returns Plain-text deposit script as a hex string.
     */
    async getPlainText() {
        const chunks = [];
        // All HEXes pushed to the script must be un-prefixed
        chunks.push(Buffer.from(this.receipt.depositor.identifierHex, "hex"));
        chunks.push(bitcoinjs_lib_1.opcodes.OP_DROP);
        const extraData = this.receipt.extraData;
        if (typeof extraData !== "undefined") {
            chunks.push(extraData.toBuffer());
            chunks.push(bitcoinjs_lib_1.opcodes.OP_DROP);
        }
        chunks.push(this.receipt.blindingFactor.toBuffer());
        chunks.push(bitcoinjs_lib_1.opcodes.OP_DROP);
        chunks.push(bitcoinjs_lib_1.opcodes.OP_DUP);
        chunks.push(bitcoinjs_lib_1.opcodes.OP_HASH160);
        chunks.push(this.receipt.walletPublicKeyHash.toBuffer());
        chunks.push(bitcoinjs_lib_1.opcodes.OP_EQUAL);
        chunks.push(bitcoinjs_lib_1.opcodes.OP_IF);
        chunks.push(bitcoinjs_lib_1.opcodes.OP_CHECKSIG);
        chunks.push(bitcoinjs_lib_1.opcodes.OP_ELSE);
        chunks.push(bitcoinjs_lib_1.opcodes.OP_DUP);
        chunks.push(bitcoinjs_lib_1.opcodes.OP_HASH160);
        chunks.push(this.receipt.refundPublicKeyHash.toBuffer());
        chunks.push(bitcoinjs_lib_1.opcodes.OP_EQUALVERIFY);
        chunks.push(this.receipt.refundLocktime.toBuffer());
        chunks.push(bitcoinjs_lib_1.opcodes.OP_CHECKLOCKTIMEVERIFY);
        chunks.push(bitcoinjs_lib_1.opcodes.OP_DROP);
        chunks.push(bitcoinjs_lib_1.opcodes.OP_CHECKSIG);
        chunks.push(bitcoinjs_lib_1.opcodes.OP_ENDIF);
        return utils_1.Hex.from(bitcoinjs_lib_1.script.compile(chunks));
    }
    /**
     * Derives a Bitcoin address for the given network for this deposit script.
     * @param bitcoinNetwork Bitcoin network the address should be derived for.
     * @returns Bitcoin address corresponding to this deposit script.
     */
    async deriveAddress(bitcoinNetwork) {
        const scriptHash = await this.getHash();
        const bitcoinJsLibNetwork = (0, bitcoin_1.toBitcoinJsLibNetwork)(bitcoinNetwork);
        if (this.witness) {
            // OP_0 <hash-length> <hash>
            const p2wshOutput = Buffer.concat([
                Buffer.from([bitcoinjs_lib_1.opcodes.OP_0, 0x20]),
                scriptHash,
            ]);
            return bitcoinjs_lib_1.payments.p2wsh({
                output: p2wshOutput,
                network: bitcoinJsLibNetwork,
            }).address;
        }
        else {
            // OP_HASH160 <hash-length> <hash> OP_EQUAL
            const p2shOutput = Buffer.concat([
                Buffer.from([bitcoinjs_lib_1.opcodes.OP_HASH160, 0x14]),
                scriptHash,
                Buffer.from([bitcoinjs_lib_1.opcodes.OP_EQUAL]),
            ]);
            return bitcoinjs_lib_1.payments.p2sh({ output: p2shOutput, network: bitcoinJsLibNetwork })
                .address;
        }
    }
}
exports.DepositScript = DepositScript;
//# sourceMappingURL=deposit.js.map